import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  useApiMutation,
  queryKeyGenerator,
  queryClient,
} from 'theSource/utils';
import {
  CategoryResponseDto,
  PlanValidationErrorsDto,
} from 'enrollment/queries/useBenefitCategoryQuery/types';
import { BenefitCategory, useConfigurationQuery } from 'theSource/queries';
import { PSModalWrapper } from '../Shared';
import { TerminateCoverageRequestDto } from 'enrollment/features/Plans/PlanCard/types';
import { UPDATE_ELECTION_QUERY_KEY } from 'enrollment/features/Plans/PlanCard/utils';
import { ResponseDto, ErrorResponseDto } from 'theSource/types';
import { GET_BENEFIT_PLANS_QUERY_KEY } from 'enrollment/queries/useBenefitPlansQuery/utils';

declare const bootstrap: any;

interface TerminatedBenefitsProps {
  coverageId: number;
  name: string;
}

export interface IneligibleBenefitsModalProps {
  enrollmentContextType: string;
  orgPlanYearContextId?: number;
  data: CategoryResponseDto | undefined;
  benefitCategories?: BenefitCategory[];
  currentIndex?: number;
  currentCategory?: string;
}

export const IneligbleBenefitsModal = ({
  enrollmentContextType,
  orgPlanYearContextId,
  data,
  benefitCategories,
  currentIndex = 0,
  currentCategory,
}: IneligibleBenefitsModalProps) => {
  const { t } = useTranslation('enrollment.benefits');
  const navigate = useNavigate();
  const categoryReload = benefitCategories
    ? benefitCategories[currentIndex]?.lookup_code
    : currentCategory;

  let validationErrors: PlanValidationErrorsDto[] | undefined = [];
  const inEligibleCoverages: TerminatedBenefitsProps[] = [];
  const [overallValidationErrors, setOverallValidationErrors] = useState<
    PlanValidationErrorsDto[]
  >([]);

  const [inEligibleButtonVisited, setInEligibleButtonVisited] = useState(false);
  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;
  const { mutate: updateElection } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    TerminateCoverageRequestDto
  >({
    queryKey: UPDATE_ELECTION_QUERY_KEY,
    queryKeysToInvalidate: queryKeyGenerator(
      `${benefitCategories ? benefitCategories[currentIndex]?.lookup_code : currentCategory}-${enrollmentContextType}`,
      Number(orgPlanYearContextId)
    ),
    method: 'put',
    endpoint: `${lumosApiHost}/v2/self_service/benefits/elect`,
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
  });

  const terminateCoverage = (params: TerminateCoverageRequestDto) => {
    updateElection(params);
    queryClient.invalidateQueries(
      GET_BENEFIT_PLANS_QUERY_KEY + '-' + params.org_benefit_id
    );
    navigate('/enrollment/benefits', {
      state: { fromCategory: categoryReload }, // Pass state here
    });
  };

  validationErrors = data?.data?.validation_errors;
  const filteredErrors: PlanValidationErrorsDto[] = [];
  if (
    validationErrors &&
    validationErrors.length > 0 &&
    !inEligibleButtonVisited
  ) {
    validationErrors.forEach((error: PlanValidationErrorsDto) => {
      const targetBenefit = data?.data.benefits.find(
        (benefit) =>
          benefit.coverage?.coverage_id === error.coverage_id && !error.hidden
      );
      if (targetBenefit && targetBenefit.coverage) {
        const coverage = targetBenefit.coverage;
        const existingBenefit = inEligibleCoverages.find(
          (plan) => plan?.coverageId === coverage.coverage_id
        );
        if (!existingBenefit) {
          inEligibleCoverages.push({
            coverageId: coverage.coverage_id,
            name: coverage.req_org_plan_name,
          });
          filteredErrors.push(error);
        }
      } else if (error.coverage_id && !error.hidden) {
        // shows validation errors for coverages on future pages. May not be needed once contribution status as current category logic is fixed
        const message = error.message.split('.')[0].split('- ')[1];
        const existingBenefit = inEligibleCoverages.find(
          (plan) => plan?.coverageId === error.coverage_id
        );
        if (message.length > 0 && !existingBenefit) {
          inEligibleCoverages.push({
            coverageId: error.coverage_id,
            name: message,
          });
          filteredErrors.push(error);
        }
      }
    });
    if (inEligibleCoverages.length > 0) {
      setTimeout(() => {
        setInEligibleButtonVisited(true);
        setOverallValidationErrors(filteredErrors);

        const combinedInEligibleText = inEligibleCoverages
          .map((plan) => {
            return `<li>${plan.name}</li>`;
          })
          .join('');
        if (
          bootstrap.Modal.getInstance(
            document.getElementById('in-eligible-modal')
          )
        ) {
          return;
        }

        const dialog = new bootstrap.Modal(
          document.getElementById('in-eligible-modal')
        );

        if (dialog) {
          document.getElementById('error_list')!.innerHTML =
            combinedInEligibleText;
          dialog.show();
        }
      }, 500);
    }
  }

  const dismissFunction = () => {
    overallValidationErrors.forEach((error) => {
      terminateCoverage({
        enrollment_context_type: enrollmentContextType,
        org_benefit_id: error.org_benefit_id,
        terminate_coverage: true,
        coverage_id: error.coverage_id,
      });
    });
  };

  return (
    <PSModalWrapper
      id="in-eligible-modal"
      message={`${t(
        'inEligibleContent'
      )}<br><br><ul id="error_list" style="list-style: disc"></ul>`}
      title={t('inEligibleTitle')}
      primaryButton={t('common:confirm')}
      onModalDismiss={dismissFunction}
    />
  );
};
